import { classSellsMonthly } from '@/utils/programs/classSellsMonthly';

/**
 * Price values as they are saved and read back (#4968).
 *
 * `0` is a real price: a free session program, drop-in, or class time. Only a
 * missing value (null, undefined, or an empty form string) means "not set".
 * Writers that used `value || null` turned a $0 price into "not set".
 */
export function toStoredPrice(value: unknown): number | null {
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  // Only a string may be parsed: `Number(true)` is 1 and `Number([5])` is 5, so a
  // request body carrying the wrong type would otherwise store a price.
  if (typeof value !== 'string') return null;
  if (value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

/**
 * The class prices a class save writes (#5826). `classes.monthly_rate` and
 * `drop_in_rate` cannot be blank, and the class form hides each box when the
 * class does not sell it, so a box cleared before it was hidden arrives blank.
 * A blank price the class does not sell is left out: a new class keeps the
 * column default and an edit keeps the saved price. Any other value is written.
 */
export function classRateColumnsToWrite(row: {
  is_open_gym?: boolean | null;
  is_session_based?: boolean | null;
  monthly_membership_enabled?: boolean | null;
  drop_in_enabled?: boolean | null;
  monthly_rate?: unknown;
  drop_in_rate?: unknown;
}): { monthly_rate?: number | null; drop_in_rate?: number | null } {
  const monthlyRate = toStoredPrice(row.monthly_rate);
  const dropInRate = toStoredPrice(row.drop_in_rate);
  const sellsMonthly = classSellsMonthly(row);
  const sellsDropIns = row.drop_in_enabled ?? true;
  return {
    ...(monthlyRate !== null || sellsMonthly ? { monthly_rate: monthlyRate } : {}),
    ...(dropInRate !== null || sellsDropIns ? { drop_in_rate: dropInRate } : {}),
  };
}

/**
 * Does this class cost something (#5237, #5829)? Only a price the class sells
 * counts: a Session Program's leftover monthly rate, or a drop-in price with
 * drop-ins off, never makes a free class look paid. `extraPrices` are the
 * program's package prices.
 */
export function classHasPaidPrice(
  row: {
    is_open_gym?: boolean | null;
    is_session_based?: boolean | null;
    monthly_membership_enabled?: boolean | null;
    drop_in_enabled?: boolean | null;
    monthly_rate?: unknown;
    drop_in_rate?: unknown;
    member_drop_in_rate?: unknown;
    session_total_price?: unknown;
  },
  extraPrices: unknown[] = [],
): boolean {
  const sellsDropIns = row.drop_in_enabled !== false;
  return [
    classSellsMonthly(row) ? row.monthly_rate : null,
    sellsDropIns ? row.drop_in_rate : null,
    sellsDropIns ? row.member_drop_in_rate : null,
    row.is_session_based === true ? row.session_total_price : null,
    ...extraPrices,
  ].some((price) => (toStoredPrice(price) ?? 0) > 0);
}

/** True only for a saved price of exactly $0 — never for a blank one. */
export function isZeroPrice(value: unknown): boolean {
  return toStoredPrice(value) === 0;
}

export const ZERO_PRICE_HINT = '$0 — families pay nothing for this.';
export const ZERO_PRICE_MEMBERSHIP_FEE_HINT = 'The annual membership fee still applies.';

/**
 * The hint under a price field while it holds $0 (plan R17). Null when the
 * field is blank or above $0. `membershipFeeRequired` is the offering's own
 * "Require Annual Gym Membership Fee" toggle as it stands in the form.
 */
export function getZeroPriceHint(
  value: unknown,
  options: { membershipFeeRequired?: boolean } = {},
): string | null {
  if (!isZeroPrice(value)) return null;
  return options.membershipFeeRequired
    ? `${ZERO_PRICE_HINT} ${ZERO_PRICE_MEMBERSHIP_FEE_HINT}`
    : ZERO_PRICE_HINT;
}

/**
 * The class's saved price for this kind of enrollment, BEFORE coupons, family or
 * athlete discounts, proration, or the membership fee (#4968 KTD5). Null for
 * enrollment types this does not cover (drop-ins, packages).
 */
export function storedClassPriceForEnrollment(
  classRow: { monthly_rate?: unknown; session_total_price?: unknown } | null | undefined,
  enrollmentType: string | null | undefined,
): number | null {
  if (!classRow) return null;
  if (enrollmentType === 'monthly') return toStoredPrice(classRow.monthly_rate);
  if (enrollmentType === 'fixed_term') return toStoredPrice(classRow.session_total_price);
  return null;
}

/**
 * A class enrollment whose saved price is $0 (#4968 R13). Such an enrollment is
 * never asked for a card by Require AutoPay or by the gym-wide "require a saved
 * payment method" setting. A $0 total that comes from a coupon or discount on a
 * paid class is NOT free here — that family is still asked for a card.
 */
export function isFreeClassEnrollment(
  classRow: { monthly_rate?: unknown; session_total_price?: unknown } | null | undefined,
  enrollmentType: string | null | undefined,
): boolean {
  return storedClassPriceForEnrollment(classRow, enrollmentType) === 0;
}
